import {
  DEFAULT_LISTING_PRICE_SETTINGS,
  fetchEdgeFunction,
  fetchListingPriceSettings,
  listingCopies,
  listingPriceLines,
  type ListingPriceSettings,
} from '@auctisync/api-client';
import { Badge, Button, HelpTooltip, LearnMoreLink } from '@auctisync/ui';
import { SalesCompStrip } from '../../components/SalesCompStrip';
import { dollarsToCents, formatCents, type Tables, type TablesInsert } from '@auctisync/shared';
import { useCallback, useEffect, useMemo, useState, type ChangeEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AiCompleteModal, type AiCompleteApplyPayload } from '../../components/AiCompleteModal';
import { BidderPreviewModal } from '../../components/BidderPreviewModal';
import { PhotoManager } from '../../components/PhotoManager';
import { readClaims, useSession } from '../../hooks/useSession';
import { compressImage } from '../../lib/compressImage';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../components/Toast';

const MAX_PHOTO_BYTES = 10 * 1024 * 1024;
const ACCEPTED_PHOTO_TYPES = 'image/jpeg,image/png,image/webp,image/heic,image/gif';

type InventoryItem = Tables<'inventory_items'>;
type ItemCategory = Pick<Tables<'item_categories'>, 'id' | 'name' | 'slug'>;
type Consignor = Pick<Tables<'consignors'>, 'id' | 'display_name'>;
type BidScale = Pick<Tables<'bid_increment_scales'>, 'id' | 'name' | 'is_default'>;
type TenantOption = Pick<Tables<'tenants'>, 'id' | 'slug' | 'name'>;

// Form state mirrors the DB row but represents money in dollars (strings for
// input-binding) and stores nullable fields as empty strings. Converted at
// submit time.
type FormState = {
  control_number: string;
  title: string;
  title_override: string;
  description: string;
  description_override: string;
  condition: string;
  category_id: string;
  consignor_id: string;
  qty_on_hand: string;
  retail_price: string;
  starting_bid: string;
  reserve: string;
  estimate_min: string;
  estimate_max: string;
  msrp: string;
  mpn: string;
  upc: string;
  sku: string;
  location_bin: string;
  location_notes: string;
  bid_increment_scale_id: string;
  is_visible: boolean;
  custom_fields: Record<string, unknown>;
};

const EMPTY_FORM: FormState = {
  control_number: '',
  title: '',
  title_override: '',
  description: '',
  description_override: '',
  condition: '',
  category_id: '',
  consignor_id: '',
  qty_on_hand: '1',
  retail_price: '',
  starting_bid: '',
  reserve: '',
  estimate_min: '',
  estimate_max: '',
  msrp: '',
  mpn: '',
  upc: '',
  sku: '',
  location_bin: '',
  location_notes: '',
  bid_increment_scale_id: '',
  is_visible: true,
  custom_fields: {},
};

function itemToForm(item: InventoryItem): FormState {
  const centsToDollars = (c: number | null) => (c == null ? '' : (c / 100).toFixed(2));
  return {
    control_number: item.control_number ?? '',
    title: item.title ?? '',
    title_override: item.title_override ?? '',
    description: item.description ?? '',
    description_override: item.description_override ?? '',
    condition: item.condition ?? '',
    category_id: item.category_id ?? '',
    consignor_id: item.consignor_id ?? '',
    qty_on_hand: String(item.qty_on_hand),
    retail_price: centsToDollars(item.retail_price_cents),
    starting_bid: centsToDollars(item.starting_bid_cents),
    reserve: centsToDollars(item.reserve_cents),
    estimate_min: centsToDollars(item.estimate_min_cents),
    estimate_max: centsToDollars(item.estimate_max_cents),
    msrp: centsToDollars(item.msrp_cents),
    mpn: item.mpn ?? '',
    upc: item.upc ?? '',
    sku: item.sku ?? '',
    location_bin: item.location_bin ?? '',
    location_notes: item.location_notes ?? '',
    bid_increment_scale_id: item.bid_increment_scale_id ?? '',
    is_visible: item.is_visible,
    custom_fields:
      item.custom_fields &&
      typeof item.custom_fields === 'object' &&
      !Array.isArray(item.custom_fields)
        ? (item.custom_fields as Record<string, unknown>)
        : {},
  };
}

function formToInsert(form: FormState, tenantId: string): TablesInsert<'inventory_items'> {
  const s = (v: string) => (v.trim() === '' ? null : v.trim());
  const d = (v: string) => (v.trim() === '' ? null : dollarsToCents(Number(v)));
  const base: TablesInsert<'inventory_items'> = {
    tenant_id: tenantId,
    control_number: s(form.control_number),
    title: s(form.title),
    title_override: s(form.title_override),
    description: s(form.description),
    description_override: s(form.description_override),
    condition: s(form.condition),
    category_id: s(form.category_id),
    consignor_id: s(form.consignor_id),
    qty_on_hand: Math.max(0, parseInt(form.qty_on_hand || '0', 10) || 0),
    retail_price_cents: d(form.retail_price),
    starting_bid_cents: d(form.starting_bid),
    reserve_cents: d(form.reserve),
    estimate_min_cents: d(form.estimate_min),
    estimate_max_cents: d(form.estimate_max),
    msrp_cents: d(form.msrp),
    mpn: s(form.mpn),
    upc: s(form.upc),
    sku: s(form.sku),
    location_bin: s(form.location_bin),
    location_notes: s(form.location_notes),
    bid_increment_scale_id: s(form.bid_increment_scale_id),
    is_visible: form.is_visible,
  };
  if (Object.keys(form.custom_fields).length > 0) {
    // custom_fields column is Json; our form state is Record<string, unknown>
    // because AI Complete can set arbitrary keys. Double-cast so exactOptional
    // doesn't reject the assignment.
    base.custom_fields = form.custom_fields as unknown as NonNullable<
      TablesInsert<'inventory_items'>['custom_fields']
    >;
  }
  return base;
}

/**
 * Inventory detail / edit / create. `:id === 'new'` flips to create mode.
 *
 * Scope (Phase 1):
 *   - All core fields edit in a single form, section-grouped
 *   - HelpTooltip on every non-obvious field
 *   - Dropdowns for category, consignor, bid_increment_scale
 *   - Soft-delete via archived_at
 *   - Auto-generation of title/description is deferred (plain inputs for now)
 *
 * Photos, documents, expenses, history, notes tabs land in follow-up steps.
 */
export function AdminInventoryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { session } = useSession();
  const claims = readClaims(session);
  const toast = useToast();
  const isCreate = id === 'new';

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [item, setItem] = useState<InventoryItem | null>(null);
  // The house's "Show on listing" defaults: a lot made from this item starts
  // with them, so the bidder preview shows what that lot would (2026-10-06).
  const [listingDefaults, setListingDefaults] = useState<ListingPriceSettings>({
    ...DEFAULT_LISTING_PRICE_SETTINGS,
  });
  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [aiBusy, setAiBusy] = useState(false);
  const [aiMsg, setAiMsg] = useState<string | null>(null);

  const [aiCompleteOpen, setAiCompleteOpen] = useState(false);
  const [tenantAiSettings, setTenantAiSettings] = useState<{
    enabled: boolean;
    fields: Record<string, boolean>;
  } | null>(null);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewPhotos, setPreviewPhotos] = useState<
    Array<{ display_url: string; caption: string | null }>
  >([]);

  const [msrpBusy, setMsrpBusy] = useState(false);
  const [msrpMsg, setMsrpMsg] = useState<string | null>(null);
  const [msrpSuggestion, setMsrpSuggestion] = useState<{
    msrp_cents: number | null;
    currency: string;
    confidence: string;
    source_url: string | null;
    source_title: string | null;
    notes: string | null;
    searches_used: number;
  } | null>(null);

  const [categories, setCategories] = useState<ItemCategory[]>([]);
  const [consignors, setConsignors] = useState<Consignor[]>([]);
  const [scales, setScales] = useState<BidScale[]>([]);

  // Super-admin-only: pick a tenant to create/edit on behalf of. Tenant
  // staff don't see this picker — their tenant comes from JWT claims.
  const [tenantOptions, setTenantOptions] = useState<TenantOption[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>(claims.tenantId ?? '');
  const needsTenantPicker = claims.isSuperAdmin && !claims.tenantId;

  // Create-flow only: photos are queued client-side until the first save
  // inserts the item row. Each File stays alongside a stable blob URL for
  // preview; URLs are revoked on removal or component unmount.
  const [queuedPhotos, setQueuedPhotos] = useState<
    { id: string; file: File; previewUrl: string }[]
  >([]);
  const [photoUploadMsg, setPhotoUploadMsg] = useState<string | null>(null);

  useEffect(() => {
    // Revoke any outstanding preview URLs on unmount. Removals during the
    // session revoke their own URLs directly.
    const snapshot = queuedPhotos;
    return () => {
      for (const p of snapshot) URL.revokeObjectURL(p.previewUrl);
    };
  }, [queuedPhotos]);

  // Load dropdowns once
  useEffect(() => {
    void (async () => {
      const [{ data: cats }, { data: cons }, { data: scs }] = await Promise.all([
        supabase
          .from('item_categories')
          .select('id, name, slug')
          .is('archived_at', null)
          .order('sort_order', { ascending: true })
          .order('name', { ascending: true }),
        supabase
          .from('consignors')
          .select('id, display_name')
          .eq('status', 'active')
          .order('display_name', { ascending: true })
          .limit(500),
        supabase
          .from('bid_increment_scales')
          .select('id, name, is_default')
          .is('archived_at', null)
          .order('is_default', { ascending: false })
          .order('name', { ascending: true }),
      ]);
      setCategories(cats ?? []);
      setConsignors(cons ?? []);
      setScales(scs ?? []);
    })();
  }, []);

  // Load tenant list for super admins acting without a primary tenant
  useEffect(() => {
    if (!needsTenantPicker) return;
    void (async () => {
      const { data } = await supabase
        .from('tenants')
        .select('id, slug, name')
        .eq('status', 'active')
        .order('name', { ascending: true });
      const list = data ?? [];
      setTenantOptions(list);
      const first = list[0];
      if (first && !selectedTenantId) {
        setSelectedTenantId(first.id);
      }
    })();
  }, [needsTenantPicker, selectedTenantId]);

  // Load item when editing
  useEffect(() => {
    if (isCreate || !id) return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const { data, error: err } = await supabase
        .from('inventory_items')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      if (cancelled) return;
      if (err) {
        setError(err.message);
      } else if (!data) {
        setError('Item not found.');
      } else {
        setItem(data);
        setForm(itemToForm(data));
        // Load the tenant's AI settings once we know which tenant owns this item.
        void (async () => {
          const { data: t } = await supabase
            .from('tenants')
            .select('ai_settings')
            .eq('id', data.tenant_id)
            .maybeSingle();
          const raw = (t?.ai_settings ?? {}) as {
            enabled?: boolean;
            fields?: Record<string, boolean>;
          };
          setTenantAiSettings({
            enabled: raw.enabled ?? true,
            fields: raw.fields ?? {},
          });
        })();
        void fetchListingPriceSettings(supabase, data.tenant_id)
          .then(setListingDefaults)
          .catch(() => undefined);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isCreate]);

  const applyAiComplete = useCallback(
    (payload: AiCompleteApplyPayload) => {
      setForm((prev) => {
        const next = { ...prev };
        const centsToDollars = (c: number) => (c / 100).toFixed(2);
        if (payload.title !== undefined) next.title = payload.title;
        if (payload.description !== undefined) next.description = payload.description;
        if (payload.condition !== undefined) next.condition = payload.condition;
        if (payload.mpn !== undefined) next.mpn = payload.mpn;
        if (payload.upc !== undefined) next.upc = payload.upc;
        if (payload.msrp_cents !== undefined) next.msrp = centsToDollars(payload.msrp_cents);
        if (payload.retail_price_cents !== undefined)
          next.retail_price = centsToDollars(payload.retail_price_cents);
        if (payload.starting_bid_cents !== undefined)
          next.starting_bid = centsToDollars(payload.starting_bid_cents);
        if (payload.reserve_cents !== undefined)
          next.reserve = centsToDollars(payload.reserve_cents);
        if (payload.estimate_min_cents !== undefined)
          next.estimate_min = centsToDollars(payload.estimate_min_cents);
        if (payload.estimate_max_cents !== undefined)
          next.estimate_max = centsToDollars(payload.estimate_max_cents);
        if (payload.category_slug) {
          const match = categories.find((c) => c.slug === payload.category_slug);
          if (match) next.category_id = match.id;
        }
        if (Object.keys(payload.customFields).length > 0) {
          next.custom_fields = { ...prev.custom_fields, ...payload.customFields };
        }
        return next;
      });
      setAiCompleteOpen(false);
      toast.success('AI suggestions applied. Review the fields and click Save to persist.');
    },
    [categories, toast],
  );

  const setField = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!form.title.trim() && !form.title_override.trim() && !form.description.trim()) {
      setError('Provide at least a title or a description.');
      return;
    }

    // tenant_id resolution:
    //   - edit: use the existing item's tenant_id (preserves ownership)
    //   - create + tenant staff: use the user's claim tenant_id
    //   - create + super admin with no claim tenant: use the picker value
    const resolvedTenantId = !isCreate
      ? (item?.tenant_id ?? claims.tenantId ?? '')
      : (claims.tenantId ?? selectedTenantId);

    if (!resolvedTenantId) {
      setError(
        needsTenantPicker
          ? 'Pick a tenant to create this item under.'
          : 'Your account has no tenant context. Sign in via a tenant account.',
      );
      return;
    }

    setSaving(true);
    if (isCreate) {
      const payload = formToInsert(form, resolvedTenantId);
      const { data, error: err } = await supabase
        .from('inventory_items')
        .insert(payload)
        .select('id, tenant_id')
        .single();
      if (err) {
        setSaving(false);
        setError(err.message);
        return;
      }

      // Upload any queued photos against the brand-new item row
      if (queuedPhotos.length > 0) {
        const newItemId = data.id;
        const newTenantId = data.tenant_id;
        let failed = 0;
        for (let i = 0; i < queuedPhotos.length; i += 1) {
          const entry = queuedPhotos[i];
          if (!entry) continue;
          setPhotoUploadMsg(`Uploading photo ${i + 1} of ${queuedPhotos.length}…`);
          const uploadErr = await uploadQueuedPhoto({
            file: entry.file,
            tenantId: newTenantId,
            inventoryItemId: newItemId,
            sortOrder: i,
          });
          if (uploadErr) failed += 1;
          URL.revokeObjectURL(entry.previewUrl);
        }
        setQueuedPhotos([]);
        setPhotoUploadMsg(null);
        if (failed > 0) {
          setError(
            `Item created, but ${failed} photo${failed === 1 ? '' : 's'} failed to upload. Add them again on the edit page.`,
          );
        }
      }

      setSaving(false);
      void navigate(`/admin/inventory/${data.id}`, { replace: true });
      return;
    }

    if (!id) {
      setSaving(false);
      return;
    }

    const patch = formToInsert(form, resolvedTenantId);
    const { error: err } = await supabase.from('inventory_items').update(patch).eq('id', id);
    setSaving(false);
    if (err) {
      setError(err.message);
    } else {
      toast.success('Changes saved.');
    }
  };

  const handleArchive = async () => {
    if (!id || isCreate || !item) return;
    const confirmMsg = item.archived_at
      ? 'Restore this item to active inventory?'
      : 'Archive this item? It will be hidden from the default list view. No historical data is deleted.';
    if (!window.confirm(confirmMsg)) return;
    setSaving(true);
    const { error: err } = await supabase
      .from('inventory_items')
      .update({ archived_at: item.archived_at ? null : new Date().toISOString() })
      .eq('id', id);
    setSaving(false);
    if (err) {
      setError(err.message);
    } else {
      void navigate('/admin/inventory');
    }
  };

  const displayTitle = useMemo(() => {
    if (isCreate) return 'New inventory item';
    return form.title_override || form.title || '(no title)';
  }, [isCreate, form.title, form.title_override]);

  if (loading) {
    return <div style={{ padding: '2rem', color: 'var(--text-muted)' }}>Loading item…</div>;
  }

  return (
    <div>
      <header style={{ marginBottom: '1.5rem' }}>
        <div style={{ marginBottom: '0.5rem' }}>
          <Link to="/admin/inventory" style={backLinkStyle}>
            ← Inventory
          </Link>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: '1rem',
            flexWrap: 'wrap',
          }}
        >
          <div>
            <div
              style={{
                fontFamily: 'var(--font-heading-active)',
                fontWeight: 600,
                fontSize: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: 'var(--brand-accent)',
                marginBottom: '0.5rem',
              }}
            >
              {isCreate ? 'Item · New' : 'Item · Inventory'}
            </div>
            <h1
              style={{
                fontFamily: 'var(--font-heading-active)',
                fontSize: 'clamp(1.6rem, 1.2rem + 1.4vw, 2rem)',
                fontWeight: 700,
                letterSpacing: '-0.02em',
                color: 'var(--text)',
                marginBottom: '0.25rem',
              }}
            >
              {displayTitle}
            </h1>
            {!isCreate && item && (
              <div
                style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}
              >
                {item.control_number && (
                  <code style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                    {item.control_number}
                  </code>
                )}
                {item.archived_at ? (
                  <Badge variant="outline">Archived</Badge>
                ) : item.is_visible ? (
                  <Badge variant="success">Visible</Badge>
                ) : (
                  <Badge variant="outline">Hidden</Badge>
                )}
              </div>
            )}
          </div>
          <LearnMoreLink helpSlug="inventory-item-fields" newTab>
            Field reference
          </LearnMoreLink>
        </div>
      </header>

      {error && <AlertBox variant="danger">{error}</AlertBox>}

      {!isCreate && item && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.75rem' }}>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              void (async () => {
                const { data } = await supabase
                  .from('inventory_photos')
                  .select('display_url, caption')
                  .eq('inventory_item_id', item.id)
                  .order('sort_order', { ascending: true });
                setPreviewPhotos(
                  (data ?? []).map((p) => ({
                    display_url: p.display_url,
                    caption: p.caption ?? null,
                  })),
                );
                setPreviewOpen(true);
              })();
            }}
          >
            👁 Preview as bidder
          </Button>
        </div>
      )}

      {!isCreate && item && tenantAiSettings?.enabled && (
        <div
          style={{
            margin: '0 0 1rem',
            padding: '0.85rem 1rem',
            borderRadius: 'var(--r-card, 0.5rem)',
            background:
              'linear-gradient(90deg, rgba(59, 130, 246, 0.08), rgba(168, 85, 247, 0.08))',
            border: '1px solid var(--admin-hairline, var(--border))',
            display: 'flex',
            gap: '1rem',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ flex: '1 1 18rem' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '0.25rem',
              }}
            >
              <span
                style={{
                  fontSize: '1rem',
                  fontWeight: 600,
                  color: 'var(--text)',
                }}
              >
                ⚡ AI Complete
              </span>
              <Badge variant="outline">New</Badge>
            </div>
            <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-muted)' }}>
              One click fills the entire form from photos — title, description, condition, specs,
              and a full pricing ladder — with a confidence score per field.
            </p>
          </div>
          <Button type="button" onClick={() => setAiCompleteOpen(true)}>
            Run AI Complete
          </Button>
        </div>
      )}

      {needsTenantPicker && isCreate && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            padding: '0.75rem 1rem',
            marginBottom: '1rem',
            borderRadius: 'var(--r-tile, 0.375rem)',
            background: 'var(--surface-muted, #fef3c7)',
            border: '1px solid var(--border, #fcd34d)',
            fontSize: '0.875rem',
            color: 'var(--text)',
            flexWrap: 'wrap',
          }}
        >
          <span>
            <Badge variant="outline">Super admin</Badge> Create on behalf of tenant:
          </span>
          <select
            value={selectedTenantId}
            onChange={(e) => setSelectedTenantId(e.target.value)}
            style={{
              padding: '0.35rem 0.6rem',
              borderRadius: 'var(--r-tile, 0.375rem)',
              border: '1px solid var(--admin-hairline, var(--border))',
              background: 'var(--surface)',
              color: 'var(--text)',
              fontSize: '0.875rem',
            }}
            aria-label="Tenant"
          >
            <option value="">— Pick a tenant —</option>
            {tenantOptions.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.slug})
              </option>
            ))}
          </select>
        </div>
      )}

      {!isCreate && (
        <div
          style={{
            fontSize: '0.8rem',
            color: 'var(--text-muted)',
            background: 'var(--surface-muted)',
            padding: '0.6rem 0.85rem',
            borderRadius: 'var(--r-tile, 0.375rem)',
            border: '1px solid var(--admin-hairline, var(--border))',
            marginBottom: '1.25rem',
          }}
        >
          <strong style={{ color: 'var(--text)' }}>Template record.</strong> Edits here affect
          <strong> new lots</strong> created from this item. Existing lots keep their own snapshot
          and are edited on the lot page — changes here never ripple to them.
        </div>
      )}

      <form
        onSubmit={(e) => {
          void handleSubmit(e);
        }}
        style={{ display: 'grid', gap: '1.5rem' }}
      >
        <Section title="Content">
          <Field
            label="Title"
            help={
              <HelpTooltip helpSlug="inventory-item-fields">
                Staff-facing title. Auto-generation from fields is planned; for now type the title
                you want.
              </HelpTooltip>
            }
          >
            <input
              type="text"
              value={form.title}
              onChange={(e) => setField('title', e.target.value)}
              style={inputStyle}
              placeholder="e.g., Milwaukee M18 Impact Driver (tool-only)"
            />
          </Field>
          <Field
            label="Title override"
            help={
              <HelpTooltip helpSlug="inventory-item-fields">
                Buyer-facing title. Wins over the auto/staff title when set.
              </HelpTooltip>
            }
          >
            <input
              type="text"
              value={form.title_override}
              onChange={(e) => setField('title_override', e.target.value)}
              style={inputStyle}
              placeholder="Leave blank to use the title above"
            />
          </Field>
          <Field
            label="Description"
            help={
              <HelpTooltip helpSlug="inventory-item-fields">
                Plain text or basic markdown. Rich-text editor arrives later.
              </HelpTooltip>
            }
          >
            <textarea
              value={form.description}
              onChange={(e) => setField('description', e.target.value)}
              rows={5}
              style={{ ...inputStyle, resize: 'vertical', minHeight: '6rem' }}
            />
          </Field>
          <Field label="Description override">
            <textarea
              value={form.description_override}
              onChange={(e) => setField('description_override', e.target.value)}
              rows={3}
              style={{ ...inputStyle, resize: 'vertical', minHeight: '4rem' }}
              placeholder="Leave blank to use the description above"
            />
          </Field>
          {!isCreate && item && (
            <AiDescriptionPanel
              item={item}
              busy={aiBusy}
              message={aiMsg}
              onGenerate={() => {
                void (async () => {
                  setAiBusy(true);
                  setAiMsg(null);
                  try {
                    // Use direct fetch (not supabase.functions.invoke) so the
                    // response body is readable on non-2xx — invoke swallows
                    // it behind a generic "Edge Function returned a non-2xx".
                    // Token and the one-refresh retry on a 401 come from the shared client.
                    const res = await fetchEdgeFunction(supabase, 'generate-item-description', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ inventory_item_id: item.id }),
                    });
                    const raw = await res.text();
                    let data: {
                      ai_description?: string;
                      model?: string;
                      generated_at?: string;
                      source_photo_ids?: string[];
                      error?: string;
                      detail?: string;
                      status?: number;
                    } = {};
                    try {
                      data = JSON.parse(raw) as typeof data;
                    } catch {
                      data = { error: raw.slice(0, 500) };
                    }
                    if (!res.ok || !data.ai_description) {
                      const parts = [
                        `HTTP ${res.status}`,
                        data.error,
                        data.detail ? `detail: ${data.detail.slice(0, 300)}` : null,
                      ].filter(Boolean);
                      setAiMsg(parts.join(' · '));
                    } else {
                      setItem({
                        ...item,
                        ai_description: data.ai_description,
                        ai_description_generated_at: data.generated_at ?? new Date().toISOString(),
                        ai_description_model: data.model ?? null,
                        ai_description_source_photo_ids: data.source_photo_ids ?? null,
                      } as InventoryItem);
                      setAiMsg(
                        'Draft ready. Use the "Copy to description" button to move it into the field above.',
                      );
                    }
                  } catch (e) {
                    setAiMsg(e instanceof Error ? e.message : String(e));
                  } finally {
                    setAiBusy(false);
                  }
                })();
              }}
              onCopy={() => {
                if (item.ai_description) {
                  setField('description', item.ai_description);
                  setAiMsg('Copied into the Description field. Review, edit, then Save.');
                }
              }}
            />
          )}
          <Field label="Condition">
            <input
              type="text"
              value={form.condition}
              onChange={(e) => setField('condition', e.target.value)}
              placeholder="e.g., New in Box, Used — Very Good"
              style={inputStyle}
            />
          </Field>
        </Section>

        <Section title="Photos">
          {isCreate ? (
            <PhotoQueue
              queued={queuedPhotos}
              onAdd={(files) => {
                const additions = files.map((file) => ({
                  id: crypto.randomUUID(),
                  file,
                  previewUrl: URL.createObjectURL(file),
                }));
                setQueuedPhotos((prev) => [...prev, ...additions]);
              }}
              onRemove={(id) => {
                setQueuedPhotos((prev) => {
                  const target = prev.find((p) => p.id === id);
                  if (target) URL.revokeObjectURL(target.previewUrl);
                  return prev.filter((p) => p.id !== id);
                });
              }}
              disabled={saving}
            />
          ) : item ? (
            <PhotoManager inventoryItemId={item.id} tenantId={item.tenant_id} />
          ) : null}
        </Section>

        <Section title="Identifiers">
          <TwoCol>
            <Field
              label="Control number"
              help={
                <HelpTooltip helpSlug="inventory-item-fields">
                  Tenant-unique internal ID (e.g., DTA-12345). Leave blank to auto-assign later.
                </HelpTooltip>
              }
            >
              <input
                type="text"
                value={form.control_number}
                onChange={(e) => setField('control_number', e.target.value)}
                style={inputStyle}
              />
            </Field>
            <Field label="MPN">
              <input
                type="text"
                value={form.mpn}
                onChange={(e) => setField('mpn', e.target.value)}
                style={inputStyle}
              />
            </Field>
            <Field label="UPC">
              <input
                type="text"
                value={form.upc}
                onChange={(e) => setField('upc', e.target.value)}
                style={inputStyle}
              />
            </Field>
            <Field label="SKU">
              <input
                type="text"
                value={form.sku}
                onChange={(e) => setField('sku', e.target.value)}
                style={inputStyle}
              />
            </Field>
          </TwoCol>
        </Section>

        {Object.keys(form.custom_fields).length > 0 && (
          <Section title="Specs">
            <p
              style={{
                fontSize: '0.8rem',
                color: 'var(--text-muted)',
                margin: '0 0 0.5rem',
              }}
            >
              Stored in <code>custom_fields</code>. Populated by AI Complete — bidders see these in
              the Specifications section on the lot page. To edit, re-run AI Complete.
            </p>
            <dl
              style={{
                display: 'grid',
                gridTemplateColumns: 'max-content minmax(0, 1fr)',
                overflowWrap: 'anywhere',
                columnGap: '1rem',
                rowGap: '0.3rem',
                margin: 0,
                fontSize: '0.9rem',
              }}
            >
              {Object.entries(form.custom_fields).map(([k, v]) => {
                const display =
                  typeof v === 'string'
                    ? v
                    : typeof v === 'number'
                      ? String(v)
                      : typeof v === 'boolean'
                        ? v
                          ? 'yes'
                          : 'no'
                        : '';
                if (!display) return null;
                return (
                  <div key={k} style={{ display: 'contents' }}>
                    <dt style={{ color: 'var(--text-muted)', textTransform: 'capitalize' }}>
                      {k.replace(/_/g, ' ')}
                    </dt>
                    <dd style={{ margin: 0, color: 'var(--text)' }}>{display}</dd>
                  </div>
                );
              })}
            </dl>
          </Section>
        )}

        <Section title="Classification">
          <TwoCol>
            <Field
              label="Category"
              help={<HelpTooltip helpSlug="item-categories">Tenant-scoped taxonomy.</HelpTooltip>}
            >
              <select
                value={form.category_id}
                onChange={(e) => setField('category_id', e.target.value)}
                style={inputStyle}
              >
                <option value="">— None —</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field
              label="Consignor"
              help={
                <HelpTooltip helpSlug="inventory-overview">
                  Leave blank for house items (no commission owed).
                </HelpTooltip>
              }
            >
              <select
                value={form.consignor_id}
                onChange={(e) => setField('consignor_id', e.target.value)}
                style={inputStyle}
              >
                <option value="">— None / house item —</option>
                {consignors.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.display_name}
                  </option>
                ))}
              </select>
            </Field>
          </TwoCol>
        </Section>

        <Section title="Quantities">
          <TwoCol>
            <Field label="Qty on hand">
              <input
                type="number"
                min={0}
                value={form.qty_on_hand}
                onChange={(e) => setField('qty_on_hand', e.target.value)}
                style={inputStyle}
              />
            </Field>
            {!isCreate && item && (
              <div
                style={{
                  display: 'flex',
                  gap: '1.5rem',
                  alignItems: 'flex-end',
                  fontSize: '0.85rem',
                  color: 'var(--text-muted)',
                }}
              >
                <div>
                  On auction: <strong>{item.qty_on_auction}</strong>
                </div>
                <div>
                  Sold: <strong>{item.qty_sold}</strong>
                </div>
              </div>
            )}
          </TwoCol>
        </Section>

        <Section title="Pricing">
          <div style={{ marginBottom: '1rem' }}>
            <SalesCompStrip
              tenantId={item?.tenant_id ?? claims.tenantId ?? selectedTenantId ?? null}
              title={form.title}
              mpn={form.mpn || null}
              categoryId={form.category_id || null}
              categoryName={categories.find((c) => c.id === form.category_id)?.name || null}
              condition={form.condition || null}
            />
          </div>
          <TwoCol>
            <Field label="Retail price">
              <DollarInput
                value={form.retail_price}
                onChange={(v) => setField('retail_price', v)}
              />
            </Field>
            <Field
              label="MSRP"
              help={
                <HelpTooltip helpSlug="ai-auto-description">
                  Manufacturer Suggested Retail Price (new, US). Click Research to let AuctiSync AI
                  look it up from the web using the item&apos;s title, MPN, or UPC.
                </HelpTooltip>
              }
            >
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <DollarInput value={form.msrp} onChange={(v) => setField('msrp', v)} />
                {!isCreate && item && (
                  <Button
                    type="button"
                    variant="outline"
                    disabled={msrpBusy}
                    onClick={() => {
                      void (async () => {
                        setMsrpBusy(true);
                        setMsrpMsg(null);
                        setMsrpSuggestion(null);
                        try {
                          // Direct so the error body stays readable; token and the
                          // one-refresh retry on a 401 come from the shared client.
                          const res = await fetchEdgeFunction(supabase, 'research-item-msrp', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ inventory_item_id: item.id }),
                          });
                          const raw = await res.text();
                          let data: {
                            msrp_cents?: number | null;
                            currency?: string;
                            confidence?: string;
                            source_url?: string | null;
                            source_title?: string | null;
                            notes?: string | null;
                            searches_used?: number;
                            error?: string;
                            detail?: string;
                          } = {};
                          try {
                            data = JSON.parse(raw) as typeof data;
                          } catch {
                            data = { error: raw.slice(0, 500) };
                          }
                          if (!res.ok || data.error) {
                            const parts = [
                              `HTTP ${res.status}`,
                              data.error,
                              data.detail ? `detail: ${data.detail.slice(0, 300)}` : null,
                            ].filter(Boolean);
                            setMsrpMsg(parts.join(' · '));
                          } else {
                            setMsrpSuggestion({
                              msrp_cents: data.msrp_cents ?? null,
                              currency: data.currency ?? 'USD',
                              confidence: data.confidence ?? 'unknown',
                              source_url: data.source_url ?? null,
                              source_title: data.source_title ?? null,
                              notes: data.notes ?? null,
                              searches_used: data.searches_used ?? 0,
                            });
                          }
                        } catch (e) {
                          setMsrpMsg(e instanceof Error ? e.message : String(e));
                        } finally {
                          setMsrpBusy(false);
                        }
                      })();
                    }}
                  >
                    {msrpBusy ? 'Researching…' : 'Research'}
                  </Button>
                )}
              </div>
              {msrpSuggestion && (
                <MsrpSuggestionPanel
                  suggestion={msrpSuggestion}
                  onApply={() => {
                    if (msrpSuggestion.msrp_cents) {
                      setField('msrp', (msrpSuggestion.msrp_cents / 100).toFixed(2));
                      setMsrpMsg(
                        'Applied. Review the value, then Save to persist. The source link stays below for reference.',
                      );
                    }
                  }}
                  onDismiss={() => {
                    setMsrpSuggestion(null);
                    setMsrpMsg(null);
                  }}
                />
              )}
              {msrpMsg && (
                <div
                  role="status"
                  style={{
                    marginTop: '0.4rem',
                    padding: '0.4rem 0.6rem',
                    borderRadius: 'var(--r-tile, 0.375rem)',
                    background: 'var(--surface-muted, #f8fafc)',
                    color: 'var(--text)',
                    border: '1px solid var(--admin-hairline, var(--border))',
                    fontSize: '0.8rem',
                  }}
                >
                  {msrpMsg}
                </div>
              )}
            </Field>
            <Field
              label="Starting bid"
              help={
                <HelpTooltip helpSlug="bid-increment-scales">
                  Opening bid applied to lots spawned from this item.
                </HelpTooltip>
              }
            >
              <DollarInput
                value={form.starting_bid}
                onChange={(v) => setField('starting_bid', v)}
              />
            </Field>
            <Field
              label="Reserve"
              help={
                <HelpTooltip helpSlug="bid-increment-scales">
                  Minimum hammer price; below this the item doesn't sell.
                </HelpTooltip>
              }
            >
              <DollarInput value={form.reserve} onChange={(v) => setField('reserve', v)} />
            </Field>
            <Field label="Estimate min (internal)">
              <DollarInput
                value={form.estimate_min}
                onChange={(v) => setField('estimate_min', v)}
              />
            </Field>
            <Field label="Estimate max (internal)">
              <DollarInput
                value={form.estimate_max}
                onChange={(v) => setField('estimate_max', v)}
              />
            </Field>
            <Field
              label="Bid increment scale"
              help={
                <HelpTooltip helpSlug="bid-increment-scales">
                  Named tiered scale. Defaults inherit from the tenant default if unset.
                </HelpTooltip>
              }
            >
              <select
                value={form.bid_increment_scale_id}
                onChange={(e) => setField('bid_increment_scale_id', e.target.value)}
                style={inputStyle}
              >
                <option value="">— Tenant default —</option>
                {scales.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                    {s.is_default ? ' (default)' : ''}
                  </option>
                ))}
              </select>
            </Field>
          </TwoCol>
        </Section>

        <Section title="Location">
          <TwoCol>
            <Field label="Bin / shelf">
              <input
                type="text"
                value={form.location_bin}
                onChange={(e) => setField('location_bin', e.target.value)}
                style={inputStyle}
              />
            </Field>
            <Field label="Location notes">
              <input
                type="text"
                value={form.location_notes}
                onChange={(e) => setField('location_notes', e.target.value)}
                style={inputStyle}
              />
            </Field>
          </TwoCol>
        </Section>

        <Section title="Visibility">
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.9rem',
              color: 'var(--text)',
            }}
          >
            <input
              type="checkbox"
              checked={form.is_visible}
              onChange={(e) => setField('is_visible', e.target.checked)}
            />
            Visible to buyers
            <HelpTooltip helpSlug="inventory-item-fields">
              When off, the item is staff-only.
            </HelpTooltip>
          </label>
        </Section>

        <div
          style={{
            display: 'flex',
            gap: '0.75rem',
            justifyContent: 'flex-end',
            alignItems: 'center',
            paddingTop: '0.5rem',
            borderTop: '1px solid var(--admin-hairline, var(--border))',
            flexWrap: 'wrap',
          }}
        >
          {!isCreate && item && (
            <Button
              type="button"
              variant={item.archived_at ? 'outline' : 'destructive'}
              onClick={() => {
                void handleArchive();
              }}
              disabled={saving}
              style={{ marginRight: 'auto' }}
            >
              {item.archived_at ? 'Restore' : 'Archive'}
            </Button>
          )}
          <Button type="button" variant="outline" asChild>
            <Link to="/admin/inventory">Cancel</Link>
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? (photoUploadMsg ?? 'Saving…') : isCreate ? 'Create item' : 'Save changes'}
          </Button>
        </div>
      </form>

      {!isCreate && item && (
        <div
          style={{
            marginTop: '2rem',
            padding: '1rem',
            borderRadius: 'var(--r-card, 0.5rem)',
            border: '1px dashed var(--border)',
            fontSize: '0.8rem',
            color: 'var(--text-muted)',
          }}
        >
          Created {new Date(item.created_at).toLocaleString()} · Updated{' '}
          {new Date(item.updated_at).toLocaleString()}
          {item.starting_bid_cents !== null && (
            <>
              {' '}
              · Starting bid <strong>{formatCents(item.starting_bid_cents)}</strong>
            </>
          )}
        </div>
      )}

      {previewOpen && (
        <BidderPreviewModal
          photos={previewPhotos}
          title={form.title_override.trim() || form.title}
          condition={form.condition}
          startingBidCents={
            form.starting_bid.trim() === '' ? null : dollarsToCents(Number(form.starting_bid))
          }
          reserveCents={form.reserve.trim() === '' ? null : dollarsToCents(Number(form.reserve))}
          description={form.description_override.trim() || form.description}
          specs={form.custom_fields}
          prices={listingPriceLines(
            listingCopies(
              {
                msrp_cents: form.msrp.trim() === '' ? null : dollarsToCents(Number(form.msrp)),
                retail_price_cents:
                  form.retail_price.trim() === ''
                    ? null
                    : dollarsToCents(Number(form.retail_price)),
                estimate_min_cents:
                  form.estimate_min.trim() === ''
                    ? null
                    : dollarsToCents(Number(form.estimate_min)),
                estimate_max_cents:
                  form.estimate_max.trim() === ''
                    ? null
                    : dollarsToCents(Number(form.estimate_max)),
              },
              listingDefaults,
            ),
          )}
          onClose={() => setPreviewOpen(false)}
        />
      )}

      {aiCompleteOpen && item && tenantAiSettings && (
        <AiCompleteModal
          inventoryItemId={item.id}
          enabledFields={tenantAiSettings.fields}
          currentItem={{
            title: item.title,
            description: item.description,
            condition: item.condition,
            category_id: item.category_id,
            mpn: item.mpn,
            upc: item.upc,
            msrp_cents: item.msrp_cents,
            retail_price_cents: item.retail_price_cents,
            starting_bid_cents: item.starting_bid_cents,
            reserve_cents: item.reserve_cents,
            estimate_min_cents: item.estimate_min_cents,
            estimate_max_cents: item.estimate_max_cents,
            custom_fields:
              item.custom_fields &&
              typeof item.custom_fields === 'object' &&
              !Array.isArray(item.custom_fields)
                ? (item.custom_fields as Record<string, unknown>)
                : null,
          }}
          onApply={applyAiComplete}
          onClose={() => setAiCompleteOpen(false)}
        />
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section
      style={{
        border: '1px solid var(--admin-hairline, var(--border))',
        borderRadius: 'var(--r-card, 0.5rem)',
        background: 'var(--surface-elev)',
        padding: '1rem 1.25rem',
      }}
    >
      <h2
        style={{
          fontSize: '0.875rem',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: 'var(--text-muted)',
          marginBottom: '0.75rem',
        }}
      >
        {title}
      </h2>
      <div style={{ display: 'grid', gap: '0.75rem' }}>{children}</div>
    </section>
  );
}

function TwoCol({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gap: '0.75rem',
        gridTemplateColumns: 'repeat(auto-fit, minmax(14rem, 1fr))',
      }}
    >
      {children}
    </div>
  );
}

function Field({
  label,
  help,
  children,
}: {
  label: string;
  help?: ReactNode;
  children: ReactNode;
}) {
  return (
    <label style={{ display: 'grid', gap: '0.35rem' }}>
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.35rem',
          fontSize: '0.8rem',
          fontWeight: 500,
          color: 'var(--text)',
        }}
      >
        {label}
        {help}
      </span>
      {children}
    </label>
  );
}

function DollarInput({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <div style={{ position: 'relative' }}>
      <span
        style={{
          position: 'absolute',
          left: '0.75rem',
          top: '50%',
          transform: 'translateY(-50%)',
          color: 'var(--text-muted)',
          fontSize: '0.875rem',
        }}
      >
        $
      </span>
      <input
        type="text"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...inputStyle, paddingLeft: '1.5rem' }}
        placeholder="0.00"
      />
    </div>
  );
}

function AlertBox({ variant, children }: { variant: 'danger' | 'success'; children: ReactNode }) {
  const palette =
    variant === 'danger'
      ? {
          bg: 'var(--surface-danger, #fef2f2)',
          fg: 'var(--text-danger, #991b1b)',
          border: 'var(--border-danger, #fecaca)',
        }
      : {
          bg: 'var(--surface-success, #f0fdf4)',
          fg: 'var(--text-success, #166534)',
          border: 'var(--border-success, #bbf7d0)',
        };
  return (
    <div
      role={variant === 'danger' ? 'alert' : 'status'}
      style={{
        padding: '0.75rem 1rem',
        borderRadius: 'var(--r-tile, 0.375rem)',
        background: palette.bg,
        color: palette.fg,
        border: `1px solid ${palette.border}`,
        marginBottom: '1rem',
        fontSize: '0.875rem',
      }}
    >
      {children}
    </div>
  );
}

function PhotoQueue({
  queued,
  onAdd,
  onRemove,
  disabled,
}: {
  queued: { id: string; file: File; previewUrl: string }[];
  onAdd: (files: File[]) => void;
  onRemove: (id: string) => void;
  disabled?: boolean;
}) {
  function handlePick(e: ChangeEvent<HTMLInputElement>) {
    const list = e.target.files;
    if (!list || list.length === 0) return;
    const accepted: File[] = [];
    for (let i = 0; i < list.length; i += 1) {
      const f = list[i];
      if (!f) continue;
      if (f.size > MAX_PHOTO_BYTES) continue;
      if (!f.type.startsWith('image/')) continue;
      accepted.push(f);
    }
    if (accepted.length > 0) onAdd(accepted);
    e.target.value = '';
  }

  return (
    <div style={{ display: 'grid', gap: '0.75rem' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
            {queued.length === 0
              ? 'No photos queued. Add some now or upload later.'
              : `${queued.length} photo${queued.length === 1 ? '' : 's'} queued — will upload on Save.`}
          </span>
          <HelpTooltip helpSlug="photo-upload-basics">
            Photos upload automatically when you create the item. First photo becomes the primary.
          </HelpTooltip>
        </div>
        <label>
          <input
            type="file"
            accept={ACCEPTED_PHOTO_TYPES}
            multiple
            disabled={disabled}
            onChange={handlePick}
            style={{ display: 'none' }}
          />
          <Button type="button" variant="outline" disabled={disabled} asChild>
            <span style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}>+ Add photos</span>
          </Button>
        </label>
      </div>

      {queued.length > 0 && (
        <div
          style={{
            display: 'grid',
            gap: '0.75rem',
            gridTemplateColumns: 'repeat(auto-fill, minmax(10rem, 1fr))',
          }}
        >
          {queued.map((entry, idx) => (
            <div
              key={entry.id}
              style={{
                position: 'relative',
                borderRadius: 'var(--r-card, 0.5rem)',
                overflow: 'hidden',
                border: '1px solid var(--admin-hairline, var(--border))',
                background: 'var(--surface-muted)',
                aspectRatio: '1 / 1',
              }}
            >
              <img
                src={entry.previewUrl}
                alt={entry.file.name}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
              {idx === 0 && (
                <span
                  style={{
                    position: 'absolute',
                    top: '0.35rem',
                    left: '0.35rem',
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    padding: '0.15rem 0.4rem',
                    borderRadius: '0.25rem',
                    background: 'var(--brand-primary)',
                    color: 'var(--brand-primary-fg)',
                  }}
                >
                  Primary
                </span>
              )}
              <button
                type="button"
                onClick={() => onRemove(entry.id)}
                disabled={disabled}
                aria-label={`Remove ${entry.file.name}`}
                style={{
                  position: 'absolute',
                  top: '0.35rem',
                  right: '0.35rem',
                  width: '1.5rem',
                  height: '1.5rem',
                  borderRadius: '0.25rem',
                  border: 'none',
                  background: 'rgba(185, 28, 28, 0.85)',
                  color: 'white',
                  fontSize: '0.9rem',
                  lineHeight: 1,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

async function uploadQueuedPhoto({
  file: raw,
  inventoryItemId,
}: {
  file: File;
  tenantId: string;
  inventoryItemId: string;
  sortOrder: number;
}): Promise<string | null> {
  if (raw.size > MAX_PHOTO_BYTES) return 'File exceeds 10 MB limit';

  // Module #10: route uploads through the upload-photo-to-r2 Edge Function
  // so create-mode queued photos end up in R2 alongside all other uploads.
  // Downsize client-side first so R2 storage stays small and downstream
  // AI calls don't choke on 5 MB phone photos.
  const file = await compressImage(raw);
  const fd = new FormData();
  fd.append('file', file);
  fd.append('inventory_item_id', inventoryItemId);

  const { data, error: fnErr } = (await supabase.functions.invoke('upload-photo-to-r2', {
    body: fd,
  })) as {
    data: { photo?: unknown; error?: string; detail?: string } | null;
    error: { message?: string } | null;
  };
  if (fnErr || data?.error || !data?.photo) {
    return data?.error ?? fnErr?.message ?? 'upload failed';
  }
  return null;
}

function MsrpSuggestionPanel({
  suggestion,
  onApply,
  onDismiss,
}: {
  suggestion: {
    msrp_cents: number | null;
    currency: string;
    confidence: string;
    source_url: string | null;
    source_title: string | null;
    notes: string | null;
    searches_used: number;
  };
  onApply: () => void;
  onDismiss: () => void;
}) {
  const hasNumber = suggestion.msrp_cents !== null && suggestion.msrp_cents > 0;
  return (
    <div
      style={{
        marginTop: '0.5rem',
        padding: '0.75rem 0.9rem',
        borderRadius: 'var(--r-card, 0.5rem)',
        background: 'var(--surface-muted, #f8fafc)',
        border: '1px dashed var(--border)',
        display: 'grid',
        gap: '0.4rem',
      }}
    >
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <Badge variant={suggestion.confidence === 'high' ? 'success' : 'outline'}>
          {hasNumber
            ? `${formatCents(suggestion.msrp_cents!)} ${suggestion.currency}`
            : 'No reliable price found'}
        </Badge>
        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
          confidence: {suggestion.confidence} · {suggestion.searches_used} web search
          {suggestion.searches_used === 1 ? '' : 'es'}
        </span>
      </div>
      {suggestion.notes && (
        <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text)' }}>{suggestion.notes}</p>
      )}
      {suggestion.source_url && (
        <a
          href={suggestion.source_url}
          target="_blank"
          rel="noreferrer"
          style={{
            fontSize: '0.8rem',
            color: 'var(--brand-primary)',
            wordBreak: 'break-all',
          }}
        >
          {suggestion.source_title ?? suggestion.source_url}
        </a>
      )}
      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem' }}>
        {hasNumber && (
          <Button type="button" variant="outline" onClick={onApply}>
            Apply to MSRP field
          </Button>
        )}
        <Button type="button" variant="outline" onClick={onDismiss}>
          Dismiss
        </Button>
      </div>
    </div>
  );
}

function AiDescriptionPanel({
  item,
  busy,
  message,
  onGenerate,
  onCopy,
}: {
  item: InventoryItem;
  busy: boolean;
  message: string | null;
  onGenerate: () => void;
  onCopy: () => void;
}) {
  const draft = item.ai_description ?? null;
  const generatedAt = item.ai_description_generated_at
    ? new Date(item.ai_description_generated_at).toLocaleString()
    : null;
  return (
    <div
      style={{
        padding: '0.9rem 1rem',
        borderRadius: 'var(--r-card, 0.5rem)',
        background: 'var(--surface-muted, #f8fafc)',
        border: '1px dashed var(--border)',
        display: 'grid',
        gap: '0.5rem',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
        <Badge variant="outline">AI draft</Badge>
        <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
          AuctiSync AI reads your photos + structured fields and drafts a buyer-ready description.
          Review before saving.
        </span>
        <HelpTooltip helpSlug="ai-auto-description">
          Drafts are produced by AuctiSync AI. Your structured fields are ground truth — the AI
          won&apos;t invent brands or specs you didn&apos;t enter.
        </HelpTooltip>
      </div>
      {draft && (
        <>
          <p
            style={{
              margin: 0,
              whiteSpace: 'pre-wrap',
              fontSize: '0.9rem',
              color: 'var(--text)',
              padding: '0.5rem 0.75rem',
              background: 'var(--surface)',
              borderRadius: 'var(--r-tile, 0.375rem)',
              border: '1px solid var(--admin-hairline, var(--border))',
            }}
          >
            {draft}
          </p>
          {generatedAt && (
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              Generated {generatedAt} · AuctiSync AI
            </div>
          )}
        </>
      )}
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <Button type="button" onClick={onGenerate} disabled={busy} variant="outline">
          {busy ? 'Drafting…' : draft ? 'Regenerate draft' : 'Draft with AI'}
        </Button>
        {draft && (
          <Button type="button" onClick={onCopy} disabled={busy} variant="outline">
            Copy to description
          </Button>
        )}
      </div>
      {message && (
        <div
          role="status"
          style={{
            padding: '0.45rem 0.65rem',
            borderRadius: 'var(--r-tile, 0.375rem)',
            background: 'var(--surface)',
            color: 'var(--text)',
            border: '1px solid var(--admin-hairline, var(--border))',
            fontSize: '0.8rem',
          }}
        >
          {message}
        </div>
      )}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.5rem 0.75rem',
  border: '1px solid var(--admin-hairline, var(--border))',
  borderRadius: 'var(--r-tile, 0.375rem)',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontSize: '0.875rem',
  fontFamily: 'inherit',
};

const backLinkStyle: React.CSSProperties = {
  color: 'var(--brand-accent)',
  fontSize: '0.875rem',
  textDecoration: 'none',
};
